import type * as React from 'react';

export interface ActivatableOptions<T extends Element> {
  onClick?: React.MouseEventHandler<T>;
  onKeyDown?: React.KeyboardEventHandler<T>;
  onKeyUp?: React.KeyboardEventHandler<T>;
}

export interface ActivatableProps<T extends Element> {
  role: 'button';
  tabIndex: number;
  onKeyDown: React.KeyboardEventHandler<T>;
  onKeyUp: React.KeyboardEventHandler<T>;
}

/**
 * SVG elements have no `click()`, so dispatch the event the browser would
 * send. Handlers then receive a real click event and can cancel it.
 */
function dispatchClick(target: Element): void {
  const view = target.ownerDocument.defaultView;
  if (!view) return;
  target.dispatchEvent(
    new view.MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      composed: true,
    }),
  );
}

/**
 * Elements with an accepted Space keydown waiting for its release, mapped to
 * the cleanup that disarms them. Held weakly and keyed by element, so the
 * call sites need no ref.
 */
const armedForSpace = new WeakMap<Element, () => void>();

function armSpace(target: Element): void {
  if (armedForSpace.has(target)) return;
  const disarmOnBlur = () => disarmSpace(target);
  target.addEventListener('blur', disarmOnBlur);
  armedForSpace.set(target, () => {
    target.removeEventListener('blur', disarmOnBlur);
    armedForSpace.delete(target);
  });
}

function disarmSpace(target: Element): void {
  armedForSpace.get(target)?.();
}

/**
 * Button semantics for a non-button host that has a click action: a tab stop,
 * the button role, Enter on key down and Space on key up (as a native button
 * does, so the release can't land on whatever the action focused next).
 * Like a native button, Space only activates when its keydown landed on this
 * element and wasn't cancelled, and focus stayed here until the release.
 * Returns nothing when `onClick` is absent, so unbound DOM is unchanged.
 * The caller keeps `onClick` on the same element.
 */
export function getActivatableProps<T extends Element>({
  onClick,
  onKeyDown,
  onKeyUp,
}: ActivatableOptions<T>): ActivatableProps<T> | Record<string, never> {
  if (!onClick) return {};

  return {
    role: 'button',
    tabIndex: 0,
    onKeyDown: (event) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || event.target !== event.currentTarget) {
        if (event.key === ' ') disarmSpace(event.currentTarget);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        dispatchClick(event.currentTarget);
      } else if (event.key === ' ') {
        event.preventDefault();
        armSpace(event.currentTarget);
      }
    },
    onKeyUp: (event) => {
      onKeyUp?.(event);
      if (event.key !== ' ') return;
      const armed = armedForSpace.has(event.currentTarget);
      disarmSpace(event.currentTarget);
      if (
        !armed ||
        event.defaultPrevented ||
        event.target !== event.currentTarget
      ) {
        return;
      }
      event.preventDefault();
      dispatchClick(event.currentTarget);
    },
  };
}
